import classnames from 'classnames'

import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import {
  getFeatureSectionTwoAnalytics,
  useFeatureSectionTwoImpressionRef,
} from '@src/components/widgets/use-ia-impression'

import { resolveSectionBackground } from '../widget-background'
import { WidgetButtons, WidgetMedia, WidgetRichText } from '../widget-content'
import { isFS2NewDesignShape } from '../widget-utils'

import styles from './feature-cards.module.scss'

import type { FeatureCardsWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'
import type { CSSProperties } from 'react'

// Non-PL `$off-white`, applied by `.fs2NewDesign` (imageWithTextProductDetailsSection.module.scss).
// Derived from the shared vocabulary so the value can't drift from `resolveSectionBackground`.
// eslint-disable-next-line i18next/no-literal-string -- CMS class-name key, not user-facing copy
const FS2_OFF_WHITE_BAND = resolveSectionBackground(['background-color--offWhite'])

/**
 * FEATURE_CARDS widget (non-DS / legacy path).
 *
 * A borderless grid of feature cards on a light band; each card stacks a large `media` photo, an
 * optional small `icon` glyph, an uppercase title, supporting copy, and optional CTAs. Desktop
 * column count tracks the item count via a CSS custom property; mobile is a horizontal scroll rail
 * (non-PL CAROUSEL parity). Styling lives in `feature-cards.module.scss`. Data comes from
 * `data.featureCardsData`.
 */
export function FeatureCardsWidgetLegacy(props: PLTypedWidgetProps<FeatureCardsWidgetData>) {
  const data = props.data.featureCardsData
  const items = data?.items ?? []
  // FS2 `feature_section_two_viewed` impression on scroll-into-view (IA-only). Hook is called
  // unconditionally (Rules of Hooks); the ref is only attached below for FS2-shaped sections.
  const impressionRef = useFeatureSectionTwoImpressionRef(getFeatureSectionTwoAnalytics(data))
  if (items.length === 0) return null

  // CMS-authored section styling. `classNames` are applied raw (resolved by global CSS, like the
  // non-PL `useDynamicStyle` flow) and known background tokens also resolve to an inline color.
  // NOTE: cast until the proto carries `classNames` (pending BE contract); remove once it lands.
  const classNames = (data as { classNames?: string[] } | undefined)?.classNames
  const isFeatureSectionTwo = isFS2NewDesignShape(items)
  // Authored `classNames` win; otherwise FS2-shaped sections get the non-PL `.fs2NewDesign` band.
  const backgroundColor = resolveSectionBackground(classNames) ?? (isFeatureSectionTwo ? FS2_OFF_WHITE_BAND : undefined)

  const section = (
    <section
      className={classnames(styles.section, classNames)}
      style={backgroundColor ? { backgroundColor } : undefined}
      data-testid="feature-cards-widget"
    >
      {data?.heading ? <h2 className={styles.heading}>{data.heading}</h2> : null}
      <div className={styles.grid} style={{ '--feature-cards-columns': items.length } as CSSProperties}>
        {items.map((item, index) => (
          // Prefer the item's title as a stable key (these CMS lists aren't reordered); index fallback
          // covers untitled cards. See review note on array-index keys across the content widgets.
          <article key={item.title ?? index} className={styles.card}>
            {/* `squareCrop` keeps every card's photo a 1:1 cover box regardless of the asset's
                intrinsic aspect ratio, matching non-PL `rowOfImageTextBlocks` (which passes no dims
                to the Image atom) — otherwise a square asset renders shorter than its siblings. */}
            <WidgetMedia media={item.media} className={styles.media} squareCrop />
            <WidgetMedia media={item.icon} className={styles.icon} />
            {item.title ? <h3 className={styles.title}>{item.title}</h3> : null}
            <WidgetRichText data={item.subTitle} className={styles.description} />
            <WidgetButtons buttons={item.buttons} className={styles.buttons} linkClassName={styles.button} />
          </article>
        ))}
      </div>
    </section>
  )

  // Only FS2-shaped sections (each card carries media + icon) emit the impression; plain
  // rowOfImageTextBlocks sections render without the ref, matching the non-PL FS2 gate.
  if (!isFeatureSectionTwo) return section
  return <div ref={impressionRef}>{section}</div>
}
